import React, { useState } from 'react';
import { BookOpen, Play, Mail, Phone, MapPin, ArrowRight, Send, Loader2, CheckCircle, AlertCircle, Download, Star, Quote, ExternalLink, FileText, ArrowUpRight, Globe, Building, Copy } from 'lucide-react';
import { Link } from 'react-router-dom';
import PromondLionLogo from '../components/PromondLionLogo';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const AUTHOR_IMG = "https://customer-assets-jai6qajn.emergentagent.net/job_cd729cf5-4469-4028-b6ba-a9c891131dbe/artifacts/dnctg08g_1780602847089.jpg";
const BOOK_COVER = "https://customer-assets.emergentagent.com/job_30b1d3cb-b799-4691-ba56-7e824b5598c4/artifacts/p185kuox_font%20cover%20jpg.jpg";

const PromondPublishing = () => {
  const [contactForm, setContactForm] = useState({ name: '', email: '', subject: 'Publishing enquiry', message: '' });
  const [status, setStatus] = useState('idle');

  const handleChange = (e) => setContactForm({ ...contactForm, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('loading');
    try {
      const res = await fetch(`${API_URL}/api/email/contact`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(contactForm) });
      setStatus(res.ok ? 'success' : 'error');
    } catch { setStatus('error'); }
  };

  const inputClass = "w-full bg-white/[0.03] border border-white/[0.06] px-5 py-4 text-sm text-cream placeholder-cream/15 font-light tracking-wide focus:outline-none focus:border-gold/30 transition-all disabled:opacity-50";
  const selectClass = "w-full bg-[#0d1f38] border border-white/[0.06] px-5 py-4 text-sm text-cream font-light tracking-wide focus:outline-none focus:border-gold/30 transition-all disabled:opacity-50 appearance-none";

  const retailers = [
    { name: 'Amazon', url: 'https://www.amazon.co.uk/dp/1918265003', icon: '📦' },
    { name: 'Books.by', url: 'https://books.by/promond-publishing/cultural-fluency-without-com', icon: '📚' },
    { name: 'Kindle', url: 'https://www.amazon.co.uk/dp/1918265003', icon: '📖' },
    { name: 'Apple Books', url: 'https://books.apple.com/gb/book/cultural-fluency-without-compromise/id6755616279', icon: '🍎' },
    { name: 'Kobo', url: 'https://www.kobo.com', icon: '📘' },
    { name: 'Other Retailers', url: '/books/cultural-fluency-without-compromise/links', icon: '🌐' },
  ];

  const pressItems = [
    { title: 'Download Media Kit', desc: 'PDF — Press materials', icon: FileText, action: 'download' },
    { title: 'Download Book One-Sheet', desc: 'PDF — Book summary', icon: FileText, action: 'download' },
    { title: 'Download Logos', desc: 'ZIP — Brand assets', icon: Copy, action: 'download' },
    { title: 'Request Review Copy', desc: 'Contact us for review', icon: Mail, action: 'link' },
  ];

  const pressQuotes = [
    { quote: "An eye-opener and a liberator... a must-read.", name: "Dr Taiwo Maraiyesa", role: "Consultant Paediatrician, NHS England" },
    { quote: "Fosters team cohesion... an amazing must-read.", name: "Dr Mirabel Osayi", role: "Portfolio General Practitioner, NHS" },
    { quote: "Gives language to what many experience but cannot easily name.", name: "Ngozi Iseh-Aghahowa, MD", role: "Paediatric Hospitalist, USA" },
    { quote: "A well thought through and well written book.", name: "Mr Muyiwa Owoniyi", role: "Consultant Ophthalmologist, NHS" },
  ];

  return (
    <div className="bg-navy min-h-screen" data-testid="promond-publishing-page">

      {/* ═══ HERO ═══ */}
      <section className="relative pt-32 pb-24 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute inset-0 bg-gradient-to-b from-[#0c1e3a] to-navy" />
          <div className="absolute top-0 right-0 w-[60%] h-full bg-gradient-to-l from-gold/[0.03] to-transparent" />
        </div>
        <div className="relative z-10 max-w-5xl mx-auto px-6 md:px-12 text-center">
          {/* Promond Lion-P logo mark */}
          <div className="mx-auto mb-6 w-20 h-20">
            <img
              src="https://customer-assets.emergentagent.com/job_cd729cf5-4469-4028-b6ba-a9c891131dbe/artifacts/542vvllq_36D0804E-79B4-464E-940E-6C2BEB2A4062.PNG"
              alt="Promond Publishing"
              className="w-20 h-20 object-contain rounded-lg"
              data-testid="promond-logo"
            />
          </div>
          <h1 className="font-serif text-5xl sm:text-6xl lg:text-[72px] text-cream font-light tracking-tighter leading-[0.92] mb-5">
            Promond Publishing
          </h1>
          <p className="text-cream/50 text-lg font-light leading-[1.7] tracking-wide max-w-2xl mx-auto mb-4">
            A global imprint publishing books that foster growth, excellence and hope across cultures, faith and professional lives.
          </p>
          <p className="text-cream/30 text-sm font-light leading-[1.8] tracking-wide max-w-2xl mx-auto mb-10">
            Promond Publishing champions titles in leadership, cultural intelligence, personal values, and professional development. We publish books that sharpen thinking, strengthen character, and equip readers for real-world leadership and work. Each publication reflects a commitment to clarity, depth, and global relevance.
          </p>
          <div className="flex flex-wrap justify-center gap-5">
            <a href="#books" className="btn-editorial border border-gold text-gold px-8 py-4 text-[13px] uppercase tracking-[0.15em] font-sans font-medium inline-flex items-center gap-3">
              <BookOpen size={16} /> <span>View Books</span>
            </a>
            <a href="#contact" className="btn-editorial border border-cream/20 text-cream/60 px-8 py-4 text-[13px] uppercase tracking-[0.15em] font-sans font-medium inline-flex items-center gap-3">
              <Mail size={16} /> <span>Contact Promond Publishing</span>
            </a>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gold/20 to-transparent" />
      </section>

      {/* ═══ PUBLISHED BOOKS ═══ */}
      <section id="books" className="py-24 relative">
        <div className="absolute inset-0 bg-white/[0.01]" />
        <div className="relative z-10 max-w-6xl mx-auto px-6 md:px-12">
          <div className="text-center mb-14">
            <span className="block text-[11px] tracking-[0.25em] uppercase text-gold font-sans font-semibold mb-4">Published Works</span>
            <h2 className="font-serif text-4xl sm:text-5xl text-cream font-light tracking-tighter leading-[1.05]">Published Books</h2>
            <p className="text-cream/30 text-sm font-light tracking-wide mt-3">Explore our catalogue of published works on leadership, culture, and global excellence.</p>
          </div>

          <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            <div className="lg:col-span-5 flex justify-center">
              <div className="relative group">
                <div className="absolute -inset-8 bg-gold/[0.06] blur-3xl group-hover:bg-gold/[0.09] transition-all duration-700" />
                <div className="absolute -bottom-4 left-1/2 -translate-x-1/2 w-[70%] h-6 bg-black/25 rounded-full blur-xl" />
                <img src={BOOK_COVER} alt="Cultural Fluency Without Compromise" className="relative w-[260px] sm:w-[300px] shadow-2xl shadow-black/40 transform -rotate-1 group-hover:rotate-0 group-hover:scale-[1.02] transition-all duration-500" data-testid="book-cover" />
              </div>
            </div>
            <div className="lg:col-span-7">
              <h3 className="font-serif text-3xl sm:text-4xl text-cream font-light tracking-tighter leading-tight mb-2">Cultural Fluency<br/><span className="text-gold">Without Compromise</span></h3>
              <p className="text-cream/25 text-sm tracking-wide mb-2">Thriving Across Cultures with Presence and Integrity</p>
              {/* ISBN NOTE: This ISBN (978-1-918265-00-3) should be verified against the homepage listing. Flag any mismatch before going live. */}
              <p className="text-cream/15 text-xs tracking-wide mb-6">By Dr Promise Monday &bull; 2025 &bull; ISBN: 978-1-918265-00-3</p>
              <p className="text-cream/40 text-sm font-light leading-[1.8] tracking-wide mb-6 max-w-lg">
                A framework for thriving across cultures without losing your identity. Introduces the Cultural Fluency Quotient (CFQ) and the concept of the "third culture" — a shared space where mutual growth replaces forced assimilation.
              </p>
              <div className="flex items-center gap-1 mb-6">
                {[1,2,3,4,5].map(s => <Star key={s} size={14} className="text-gold fill-gold" />)}
                <span className="text-cream/20 text-xs ml-2 tracking-wide">Verified reviews</span>
              </div>
              <div className="flex flex-wrap gap-4">
                <a href="https://books.by/promond-publishing/cultural-fluency-without-com" target="_blank" rel="noopener noreferrer" className="btn-editorial border border-gold text-gold px-7 py-3.5 text-[12px] uppercase tracking-[0.12em] font-sans font-medium inline-flex items-center gap-2">
                  <span>Order Paperback</span> <ArrowUpRight size={14} />
                </a>
                <Link to="/books/cultural-fluency-without-compromise/read" className="border-b border-cream/20 pb-1 text-[12px] uppercase tracking-[0.12em] text-cream/50 font-sans hover:border-gold hover:text-gold transition-all duration-500">
                  Read Online
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ PRESS QUOTES ═══ */}
      <section className="py-20 relative overflow-hidden">
        <div className="absolute top-8 left-1/2 -translate-x-1/2 text-[160px] font-serif text-gold/[0.03] leading-none select-none pointer-events-none" aria-hidden="true">&ldquo;</div>
        <div className="max-w-6xl mx-auto px-6 md:px-12 relative z-10">
          <span className="block text-[11px] tracking-[0.25em] uppercase text-gold font-sans font-semibold mb-10 text-center">Critical Acclaim</span>
          <div className="grid md:grid-cols-2 gap-4">
            {pressQuotes.map((q, i) => (
              <div key={i} className="bg-white/[0.02] border border-white/[0.05] p-7 hover:border-gold/20 transition-all duration-500">
                <Quote size={14} className="text-gold/20 mb-3" />
                <p className="text-cream/60 font-serif text-lg font-light italic tracking-tight leading-relaxed mb-4">&ldquo;{q.quote}&rdquo;</p>
                <div className="border-t border-white/[0.04] pt-3">
                  <p className="text-cream/50 text-xs font-medium tracking-wide">{q.name}</p>
                  <p className="text-cream/20 text-[10px] tracking-wide">{q.role}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ PRESS & PROMO ═══ */}
      <section className="py-20 relative">
        <div className="absolute inset-0 bg-white/[0.01]" />
        <div className="relative z-10 max-w-4xl mx-auto px-6 md:px-12">
          <div className="text-center mb-12">
            <span className="block text-[11px] tracking-[0.25em] uppercase text-gold font-sans font-semibold mb-4">Resources</span>
            <h2 className="font-serif text-3xl sm:text-4xl text-cream font-light tracking-tighter">Press & Promo</h2>
            <p className="text-cream/30 text-sm font-light tracking-wide mt-3">Download media assets, press kits, and promotional materials.</p>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            {pressItems.map((item, i) => (
              <a key={i} href={item.action === 'link' ? '#contact' : '#'} className="bg-white/[0.02] border border-white/[0.05] p-6 flex items-center gap-4 hover:border-gold/25 transition-all duration-500 group" data-testid={`press-item-${i}`}>
                <div className="w-10 h-10 bg-white/[0.03] border border-white/[0.04] flex items-center justify-center flex-shrink-0">
                  <item.icon size={16} className="text-cream/30" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-cream/70 text-sm font-medium tracking-wide">{item.title}</h3>
                  <p className="text-cream/20 text-xs tracking-wide">{item.desc}</p>
                </div>
                <Download size={14} className="text-cream/15 group-hover:text-gold transition-colors flex-shrink-0" />
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ WHERE TO BUY ═══ */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <div className="text-center mb-12">
            <span className="block text-[11px] tracking-[0.25em] uppercase text-gold font-sans font-semibold mb-4">Retailers</span>
            <h2 className="font-serif text-3xl sm:text-4xl text-cream font-light tracking-tighter">Where to Buy</h2>
            <p className="text-cream/30 text-sm font-light tracking-wide mt-3">Get our books from your favourite retailer worldwide.</p>
          </div>
          <div className="grid grid-cols-3 md:grid-cols-6 gap-4">
            {retailers.map((r) => (
              <a key={r.name} href={r.url} target="_blank" rel="noopener noreferrer" className="bg-white/[0.02] border border-white/[0.05] p-5 text-center hover:border-gold/25 hover:bg-white/[0.04] transition-all duration-500 group" data-testid={`retailer-${r.name.toLowerCase().replace(/\s/g, '-')}`}>
                <span className="text-2xl block mb-2">{r.icon}</span>
                <p className="text-cream/50 text-xs tracking-wide group-hover:text-gold transition-colors">{r.name}</p>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ BOOK TRAILER ═══ */}
      <section id="videos" className="py-20 relative">
        <div className="absolute inset-0 bg-white/[0.01]" />
        <div className="relative z-10 max-w-3xl mx-auto px-6 md:px-12">
          <div className="text-center mb-10">
            <span className="block text-[11px] tracking-[0.25em] uppercase text-gold font-sans font-semibold mb-4">Media</span>
            <h2 className="font-serif text-3xl text-cream font-light tracking-tighter">Book Trailer</h2>
          </div>
          <div className="bg-white/[0.02] border border-white/[0.05] overflow-hidden">
            <div className="aspect-video">
              <iframe src="https://www.youtube.com/embed/tPp7u2nEzak" title="Book Trailer — Cultural Fluency Without Compromise" className="w-full h-full" frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media" allowFullScreen />
            </div>
          </div>
        </div>
      </section>

      {/* ═══ AUTHOR ═══ */}
      <section className="py-20">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-4 flex justify-center lg:justify-start">
              <div className="relative">
                <div className="absolute -inset-4 bg-gold/[0.04] blur-2xl" />
                <img src={AUTHOR_IMG} alt="Dr Promise Monday" className="relative w-52 shadow-xl object-cover object-top" />
              </div>
            </div>
            <div className="lg:col-span-8">
              <span className="block text-[11px] tracking-[0.25em] uppercase text-gold font-sans font-semibold mb-4">The Author</span>
              <h2 className="font-serif text-3xl sm:text-4xl text-cream font-light tracking-tighter mb-4">Dr Promise Monday</h2>
              <p className="text-cream/40 text-sm font-light leading-[1.8] tracking-wide mb-4">
                Paediatrician, cultural fluency strategist, and founder of Third Culture Leadership. Promise has worked across healthcare systems, corporate environments, and community organisations across multiple countries.
              </p>
              <p className="text-cream/30 text-sm font-light leading-[1.8] tracking-wide mb-8">
                His work focuses on equipping professionals to lead effectively across cultural difference — building bridges without blurring values. He is available for speaking engagements, workshops, and consulting.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/speaking" className="btn-editorial border border-gold text-gold px-6 py-3 text-[12px] uppercase tracking-[0.12em] font-sans font-medium inline-flex items-center gap-2">
                  <span>Book for Speaking</span> <ArrowRight size={14} />
                </Link>
                <Link to="/about" className="border-b border-cream/20 pb-1 text-[12px] uppercase tracking-[0.12em] text-cream/50 font-sans hover:border-gold hover:text-gold transition-all duration-500">
                  Full Bio
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ PUBLISHING DETAILS + CONTACT ═══ */}
      <section id="contact" className="py-24 relative">
        <div className="absolute inset-0 bg-white/[0.015]" />
        <div className="relative z-10 max-w-6xl mx-auto px-6 md:px-12">
          <div className="text-center mb-14">
            <span className="block text-[11px] tracking-[0.25em] uppercase text-gold font-sans font-semibold mb-4">Get In Touch</span>
            <h2 className="font-serif text-4xl sm:text-5xl text-cream font-light tracking-tighter">Contact Promond Publishing</h2>
            <p className="text-cream/30 text-sm font-light tracking-wide mt-3">Get in touch for publishing enquiries, bulk orders, or media requests.</p>
          </div>

          <div className="grid lg:grid-cols-12 gap-12">
            {/* Publishing Details */}
            <div className="lg:col-span-5">
              <div className="bg-white/[0.02] border border-white/[0.05] p-8 mb-6">
                <span className="block text-[10px] tracking-[0.2em] uppercase text-gold/60 mb-6">Publishing Details</span>
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <Building size={16} className="text-gold/40 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-cream/20 text-[10px] uppercase tracking-[0.15em] mb-1">Imprint</p>
                      <p className="text-cream/60 text-sm font-light">Promond Publishing</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <MapPin size={16} className="text-gold/40 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-cream/20 text-[10px] uppercase tracking-[0.15em] mb-1">Address</p>
                      <p className="text-cream/60 text-sm font-light leading-relaxed">
                        Promond Publishing<br />
                        61 Bridge Street<br />
                        Kington HR5 3DJ<br />
                        United Kingdom
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <Mail size={16} className="text-gold/40 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-cream/20 text-[10px] uppercase tracking-[0.15em] mb-1">Email</p>
                      <a href="mailto:contact@thirdcultureleadership.co.uk" className="text-cream/60 text-sm font-light hover:text-gold transition-colors">contact@thirdcultureleadership.co.uk</a>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <Phone size={16} className="text-gold/40 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-cream/20 text-[10px] uppercase tracking-[0.15em] mb-1">Phone</p>
                      <a href="tel:+447359994352" className="text-cream/60 text-sm font-light hover:text-gold transition-colors">+44 7359 994352</a>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-7">
              <div className="bg-white/[0.02] border border-white/[0.05] p-8">
                <span className="block text-[10px] tracking-[0.2em] uppercase text-gold/60 mb-6">Send a Message</span>
                {status === 'success' ? (
                  <div className="text-center py-10">
                    <CheckCircle size={28} className="mx-auto text-green-400 mb-4" />
                    <p className="text-cream font-serif text-xl font-light tracking-tight mb-2">Enquiry Sent</p>
                    <p className="text-cream/30 text-sm tracking-wide mb-6">We'll respond within 2 business days.</p>
                    <button onClick={() => { setStatus('idle'); setContactForm({ name: '', email: '', subject: 'Publishing enquiry', message: '' }); }} className="text-gold text-[11px] uppercase tracking-[0.12em] hover:text-gold-light">
                      Send another <ArrowRight size={10} className="inline ml-1" />
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} data-testid="promond-contact-form">
                    {status === 'error' && <div className="flex items-center gap-2 bg-red-500/10 border border-red-500/20 p-3 mb-5"><AlertCircle size={14} className="text-red-400" /><p className="text-red-300 text-xs">Failed to send. Try again.</p></div>}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      <div>
                        <label className="block text-[10px] text-cream/20 mb-2 uppercase tracking-[0.2em]">Name *</label>
                        <input type="text" name="name" value={contactForm.name} onChange={handleChange} required disabled={status === 'loading'} className={inputClass} placeholder="Your name" />
                      </div>
                      <div>
                        <label className="block text-[10px] text-cream/20 mb-2 uppercase tracking-[0.2em]">Email *</label>
                        <input type="email" name="email" value={contactForm.email} onChange={handleChange} required disabled={status === 'loading'} className={inputClass} placeholder="you@example.com" />
                      </div>
                    </div>
                    <div className="mb-4">
                      <label className="block text-[10px] text-cream/20 mb-2 uppercase tracking-[0.2em]">Subject *</label>
                      <select name="subject" value={contactForm.subject} onChange={handleChange} disabled={status === 'loading'} className={selectClass} data-testid="promond-subject-select" style={{colorScheme: 'dark'}}>
                        <option value="Publishing enquiry" className="bg-[#0d1f38] text-white">Publishing enquiry</option>
                        <option value="Bulk orders" className="bg-[#0d1f38] text-white">Bulk orders</option>
                        <option value="Media" className="bg-[#0d1f38] text-white">Media</option>
                        <option value="Partnerships" className="bg-[#0d1f38] text-white">Partnerships</option>
                        <option value="Other" className="bg-[#0d1f38] text-white">Other</option>
                      </select>
                    </div>
                    <div className="mb-6">
                      <label className="block text-[10px] text-cream/20 mb-2 uppercase tracking-[0.2em]">Message *</label>
                      <textarea name="message" value={contactForm.message} onChange={handleChange} required disabled={status === 'loading'} rows="5" className={`${inputClass} resize-none`} placeholder="Your message..." />
                    </div>
                    <button type="submit" disabled={status === 'loading'} className="btn-editorial border border-gold text-gold px-8 py-4 text-[13px] uppercase tracking-[0.15em] font-sans font-medium inline-flex items-center gap-3 disabled:opacity-40" data-testid="promond-submit-btn">
                      {status === 'loading' ? <><Loader2 size={14} className="animate-spin" /><span>Sending...</span></> : <><span>Send Message</span> <Send size={14} /></>}
                    </button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ BACK TO SITE ═══ */}
      <section className="py-14">
        <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
          <Link to="/books" className="border-b border-cream/20 pb-1 text-[13px] uppercase tracking-[0.15em] text-cream/50 font-sans hover:border-gold hover:text-gold transition-all duration-500 inline-flex items-center gap-2">
            Explore All Books <ArrowUpRight size={14} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default PromondPublishing;
